import { useState, useEffect } from 'react';
import { tokenManager } from '../../utils/tokenManager';
import type { TokenInfo } from '../../utils/tokenManager';
import { blockchainService } from '../../services/blockchainService';
import WalletStatus from '../wallet/WalletStatus';

interface PrivacyWalletProps {
  userAddress: string | null;
}

interface PrivacyAsset extends TokenInfo {
  id: string;
  balance: string;
  privateBalance: string;
  usdValue: number;
}

interface PrivacyTransaction {
  id: string;
  type: 'deposit' | 'withdraw' | 'transfer' | 'mint';
  amount: string;
  asset: string;
  status: 'pending' | 'completed' | 'failed';
  timestamp: number;
  proof?: string;
  isPrivate: boolean;
}

const PrivacyWallet: React.FC<PrivacyWalletProps> = ({ userAddress }) => {
  const [assets, setAssets] = useState<PrivacyAsset[]>([]);
  const [transactions, setTransactions] = useState<PrivacyTransaction[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string>('');
  const [transferAmount, setTransferAmount] = useState<string>('');
  const [transferAddress, setTransferAddress] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (userAddress) {
      loadUserAssets();
      loadTransactionHistory();
    } else {
      setAssets([]);
      setTransactions([]);
    }
  }, [userAddress]);

  const loadUserAssets = async () => {
    if (!userAddress) return;

    try {
      // 检查区块链服务是否可用
      if (!blockchainService.isConnected()) {
        console.warn('区块链服务未连接，使用模拟资产数据');
        // 如果区块链服务不可用，回退到模拟数据
        const tokenBalances = await tokenManager.getTokenBalances(userAddress);
        const supportedTokens = tokenManager.getSupportedTokens();

        const publicAssets: PrivacyAsset[] = supportedTokens.map(token => {
          const balance = tokenBalances.find(b => b.symbol === token.symbol);
          return {
            ...token,
            id: token.symbol,
            balance: balance?.balance || '0',
            privateBalance: '0',
            usdValue: parseFloat(balance?.balance || '0') * (token.price || 0)
          };
        });

        const privateAssets: PrivacyAsset[] = supportedTokens.map(token => {
          const privateToken = tokenManager.getPrivateToken(token.symbol);
          const balance = tokenBalances.find(b => b.symbol === token.symbol);
          if (!privateToken) return null;
          
          return {
            ...privateToken,
            id: privateToken.symbol,
            balance: '0', // 隐私代币不显示公开余额
            privateBalance: '0', // 隐私余额需要从隐私网络获取
            usdValue: 0
          };
        }).filter(Boolean) as PrivacyAsset[];

        setAssets([...publicAssets, ...privateAssets]);
        return;
      }

      // 使用真实的区块链数据
      const blockchainBalances = await blockchainService.getAllTokenBalances(userAddress);
      const supportedTokens = tokenManager.getSupportedTokens();

      // 构建公开资产列表
      const publicAssets: PrivacyAsset[] = supportedTokens.map(token => {
        const blockchainBalance = blockchainBalances.find(b => b.symbol === token.symbol);
        return {
          ...token,
          id: token.symbol,
          balance: blockchainBalance?.balance || '0',
          privateBalance: '0',
          usdValue: parseFloat(blockchainBalance?.balance || '0') * (token.price || 0)
        };
      });

      // 构建隐私资产列表（暂时使用模拟数据）
      const privateAssets: PrivacyAsset[] = supportedTokens.map(token => {
        const privateToken = tokenManager.getPrivateToken(token.symbol);
        if (!privateToken) return null;
        
        return {
          ...privateToken,
          id: privateToken.symbol,
          balance: '0', // 隐私代币不显示公开余额
          privateBalance: '0', // 隐私余额需要从隐私网络获取
          usdValue: 0
        };
      }).filter(Boolean) as PrivacyAsset[];

      setAssets([...publicAssets, ...privateAssets]);
    } catch (error) {
      console.error('加载用户资产失败:', error);
      setAssets([]);
    }
  };

  const loadTransactionHistory = async () => {
    // 所有连接钱包用户的交易记录都是空白的
    setTransactions([]);
  };

  const handleMintPrivate = async (assetId: string) => {
    if (!userAddress) {
      alert('Please connect wallet first');
      return;
    }

    const asset = assets.find(a => a.symbol === assetId);
    if (!asset || asset.isPrivate) return;

    // 检测用户是否有足够的资产
    const hasAsset = parseFloat(asset.balance) > 0;
    
    if (!hasAsset) {
      alert('Operation failed: Insufficient assets for this operation');
      return;
    }

    // 如果有资产，提示没有足够的TRN支付手续费
    alert('Operation failed: Insufficient TRN to pay fees');
  };

  const handleBurnPrivate = async (assetId: string) => {
    if (!userAddress) {
      alert('Please connect wallet first');
      return;
    }

    const asset = assets.find(a => a.symbol === assetId);
    if (!asset || !asset.isPrivate) return;

    // 检测用户是否有足够的隐私资产
    const hasPrivateAsset = parseFloat(asset.privateBalance) > 0;
    
    if (!hasPrivateAsset) {
      alert('Operation failed: Insufficient assets for this operation');
      return;
    }

    // 如果有资产，提示没有足够的TRN支付手续费
    alert('Operation failed: Insufficient TRN to pay fees');
  };

  const handlePrivateTransfer = async () => {
    if (!userAddress) {
      alert('Please connect wallet first');
      return;
    }

    if (!selectedAsset || !transferAmount || !transferAddress) {
      alert('Please fill in all required information');
      return;
    }

    const asset = assets.find(a => a.symbol === selectedAsset);
    if (!asset) return;

    // 检测用户是否有可用的资产
    const hasAsset = asset.isPrivate ? 
      parseFloat(asset.privateBalance) > 0 : 
      parseFloat(asset.balance) > 0;
    
    if (!hasAsset) {
      alert('Operation failed: No available assets or TRN');
      return;
    }

    // 如果有资产，提示没有足够的TRN支付手续费
    alert('Operation failed: Insufficient TRN to pay fees');
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit': return 'fas fa-arrow-down text-green-600';
      case 'withdraw': return 'fas fa-arrow-up text-red-600';
      case 'transfer': return 'fas fa-exchange-alt text-blue-600';
      case 'mint': return 'fas fa-plus text-purple-600';
      default: return 'fas fa-circle text-gray-400';
    }
  };

  const getTransactionText = (type: string) => {
    switch (type) {
      case 'deposit': return 'Deposit';
      case 'withdraw': return 'Withdraw';
      case 'transfer': return 'Transfer';
      case 'mint': return 'Mint';
      default: return 'Unknown';
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  const publicAssets = assets.filter(asset => !asset.isPrivate);
  const privateAssets = assets.filter(asset => asset.isPrivate);

  return (
    <div className="space-y-6">
      {/* 钱包状态 */}
      <WalletStatus userAddress={userAddress} />

      {/* 资产概览 */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Assets Overview</h3>
        
        {/* 公开资产 */}
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">Public Assets</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {publicAssets.map((asset) => (
              <div key={asset.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <i className={`${asset.icon} text-lg ${asset.color}`}></i>
                    <span className="font-medium text-gray-900">{asset.symbol}</span>
                  </div>
                  <button
                    onClick={() => handleMintPrivate(asset.symbol)}
                    disabled={isProcessing}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    Mint Private
                  </button>
                </div>
                <div className="text-sm text-gray-600">
                  <div>Balance: {parseFloat(asset.balance).toFixed(4)} {asset.symbol}</div>
                  <div>Value: ${asset.usdValue.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 隐私资产 */}
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3">Privacy Assets</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {privateAssets.map((asset) => (
              <div key={asset.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <i className={`${asset.icon} text-lg ${asset.color}`}></i>
                    <span className="font-medium text-gray-900">{asset.symbol}</span>
                  </div>
                  <button
                    onClick={() => handleBurnPrivate(asset.symbol)}
                    disabled={isProcessing}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    Burn to Public
                  </button>
                </div>
                <div className="text-sm text-gray-600">
                  <div>Private Balance: {parseFloat(asset.privateBalance).toFixed(4)} {asset.symbol}</div>
                  <div className="text-xs text-gray-500">Private balance is not publicly displayed</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 隐私转账 */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Privacy Transfer</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Asset</label>
            <select
              value={selectedAsset}
              onChange={(e) => setSelectedAsset(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select asset to transfer</option>
              {assets.map((asset) => (
                <option key={asset.id} value={asset.symbol}>
                  {asset.symbol} - {asset.isPrivate ? 'Private' : 'Public'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Amount</label>
            <input
              type="number"
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
              placeholder="Enter transfer amount"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Address</label>
            <input
              type="text"
              value={transferAddress}
              onChange={(e) => setTransferAddress(e.target.value)}
              placeholder="Enter recipient address"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <button
              onClick={handlePrivateTransfer}
              disabled={isProcessing || !selectedAsset || !transferAmount || !transferAddress}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Processing...' : 'Execute Privacy Transfer'}
            </button>
          </div>
        </div>
      </div>

      {/* 交易历史 */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h3>
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <i className="fas fa-history text-4xl mb-4"></i>
            <p>No transaction records</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <i className={`${getTransactionIcon(transaction.type)} text-xl`}></i>
                  <div>
                    <div className="font-medium text-gray-900">
                      {getTransactionText(transaction.type)} {transaction.amount} {transaction.asset}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatTime(transaction.timestamp)}
                      {transaction.isPrivate && <span className="ml-2 text-purple-600">Privacy</span>}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${
                    transaction.status === 'completed' ? 'text-green-600' :
                    transaction.status === 'failed' ? 'text-red-600' : 'text-yellow-600'
                  }`}>
                    {transaction.status === 'completed' ? 'Completed' :
                     transaction.status === 'failed' ? 'Failed' : 'Processing'}
                  </div>
                  {transaction.proof && (
                    <div className="text-xs text-gray-400 mt-1">
                      Proof: {transaction.proof.substring(0, 8)}...
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacyWallet;